import React, { useState } from "react";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { updateDailyStock } from "../updateDailyStock";
import { logAudit } from "../auditLogger";

import Layout from "../components/Layout";

const InwardUpload = () => {
  const [preview, setPreview] = useState([]);
  const today = new Date().toISOString().slice(0, 10);

  /* ================= FILE HANDLER ================= */

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split("\n").filter((l) => l.trim() !== "");
      if (lines.length < 2) return;

      const headers = lines[0].split(",").map((h) => h.trim());

      const rows = lines.slice(1).map((line) => {
        const values = line.split(",");
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = values[i]?.trim();
        });
        return obj;
      });

      // Aggregate SKU-wise
      const map = {};

      rows.forEach((r) => {
        const sku = (r.SKU || "").trim().toUpperCase();
        const qty = Number(r.Qty || r.Quantity || 0);
        const rate = Number(r["Avg Rate"] || r.Rate || 0);

        if (!sku || qty <= 0) return;

        if (!map[sku]) {
          map[sku] = { qty: 0, value: 0 };
        }

        map[sku].qty += qty;
        map[sku].value += qty * rate;
      });

      const final = Object.keys(map).map((sku) => {
        const qty = map[sku].qty;
        const value = map[sku].value;
        return {
          sku,
          quantity: qty,
          rate: qty > 0 ? value / qty : 0,
        };
      });

      setPreview(final);
    };

    reader.readAsText(file);
  };

  /* ================= CONFIRM UPLOAD ================= */

  const confirmUpload = async () => {
    for (let r of preview) {
      const docId = `${today}_${r.sku}`;

      await setDoc(
        doc(db, "inward_register", docId),
        {
          inwardDate: today,
          sku: r.sku,
          quantity: r.quantity,
          purchaseBasicRate: r.rate,
          source: "ITC LTD",
          reference: "Excel Upload",
          uploadedBy: auth.currentUser?.uid,
          uploadedAt: Timestamp.now(),
          fileName: "excel_upload",
          editable: true,
        },
        { merge: true }
      );

      await updateDailyStock(today, r.sku);

      await logAudit({
        module: "INWARD",
        action: "CREATE",
        refCollection: "inward_register",
        refDocId: docId,
        sku: r.sku,
        date: today,
        oldData: null,
        newData: r,
        role: "OPERATOR",
      });
    }

    alert("Batch manifest ingested successfully. System logs updated.");
    setPreview([]);
  };

  return (
    <Layout>
      <div className="layout-content">
        {/* HEADER */}
        <div style={{ marginBottom: 40 }}>
            <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Batch Inward Ingestion</h1>
            <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Rapidly provision warehouse stock using standardized CSV manifest protocols.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: preview.length > 0 ? "1fr 1fr" : "1fr", gap: 32 }}>
            <div className="premium-card" style={{ padding: 40, textAlign: "center", border: "2px dashed var(--border)", background: "var(--surface-hover)", cursor: "pointer", transition: "all 0.2s" }} onMouseOver={(e) => e.currentTarget.style.borderColor = "var(--primary)"} onMouseOut={(e) => e.currentTarget.style.borderColor = "var(--border)"}>
                <div style={{ fontSize: 48, marginBottom: 24 }}>📑</div>
                <h3 style={{ fontSize: 20, marginBottom: 12 }}>Drop Manifest File</h3>
                <p style={{ color: "var(--text-muted)", fontSize: 14, marginBottom: 32 }}>Upload a .csv file containing SKU, Qty, and Rate headers.</p>
                
                <input
                    type="file"
                    id="csv_up"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={handleFile}
                    onClick={(e) => (e.target.value = null)}
                />
                <label htmlFor="csv_up" className="btn-primary" style={{ padding: "14px 40px", cursor: "pointer", borderRadius: 12 }}>
                   BROWSE FILESYSTEM
                </label>
            </div>

            {preview.length > 0 && (
                <div className="premium-card" style={{ padding: 0, overflow: "hidden", animation: "fadeIn 0.3s ease-out" }}>
                    <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-hover)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 13, fontWeight: 800 }}>INGESTION PREVIEW</span>
                        <div className="badge badge-success">{preview.length} SKUs Detected</div>
                    </div>
                    
                    <div style={{ maxHeight: 400, overflowY: "auto" }}>
                        <table className="enterprise-table">
                            <thead>
                                <tr>
                                    <th style={{ paddingLeft: 24 }}>PRODUCT NODE</th>
                                    <th style={{ textAlign: "center" }}>VOLUME</th>
                                    <th style={{ textAlign: "right", paddingRight: 24 }}>EST. RATE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.map((p, i) => (
                                    <tr key={i}>
                                        <td style={{ paddingLeft: 24, fontWeight: 700, color: "var(--primary)" }}>{p.sku}</td>
                                        <td style={{ textAlign: "center", fontWeight: 800 }}>{p.quantity.toLocaleString()}</td>
                                        <td style={{ textAlign: "right", paddingRight: 24, fontWeight: 600 }}>₹{Number(p.rate || 0).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ padding: "24px", background: "var(--surface)", borderTop: "1px solid var(--border)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Aggregate Volume</div>
                                <div style={{ fontSize: 24, fontWeight: 900 }}>{preview.reduce((s, r) => s + (Number(r.quantity) || 0), 0).toLocaleString()} <span style={{ fontSize: 12, opacity: 0.5 }}>PACKETS</span></div>
                            </div>
                            <button 
                                onClick={confirmUpload} 
                                className="btn-primary"
                                style={{ padding: "14px 32px", fontSize: 15 }}
                            >
                                AUTHORIZE SYSTEM INGESTION
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
      </div>
    </Layout>
  );
};

export default InwardUpload;
